/**
 * Shared Button component matching the Commonplace Design System.
 *
 * Variants:
 *   primary   — black fill, white text
 *   secondary — white fill, black border
 *   outline   — transparent, gray border
 *   danger    — red border, red text
 *   ghost     — no border, black text
 */
import React from 'react';
import {
  TouchableOpacity, Text, StyleSheet, ActivityIndicator,
  ViewStyle, TextStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { fonts, RD } from '../theme/colors';

type Variant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | 'maroon';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: Variant;
  size?: Size;
  icon?: keyof typeof Ionicons.glyphMap;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: ViewStyle;
}

const HEIGHT: Record<Size, number> = { sm: 40, md: 48, lg: 54 };
const FONT_SIZE: Record<Size, number> = { sm: 13, md: 15, lg: 16 };
const ICON_SIZE: Record<Size, number> = { sm: 16, md: 18, lg: 20 };
const PILL_RADIUS: Record<Size, number> = { sm: 12, md: 14, lg: 16 };

export function Button({
  title, onPress, variant = 'primary', size = 'md',
  icon, loading, disabled, fullWidth, style,
}: ButtonProps) {
  const v = VARIANTS[variant];
  const h = HEIGHT[size];
  const fs = FONT_SIZE[size];
  const is = ICON_SIZE[size];
  const br = PILL_RADIUS[size];

  return (
    <TouchableOpacity
      style={[
        styles.base,
        { height: h, borderRadius: br },
        v.container,
        fullWidth && styles.fullWidth,
        disabled && styles.disabled,
        style,
      ]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.75}
    >
      {loading ? (
        <ActivityIndicator color={v.text.color as string} />
      ) : (
        <>
          {icon && <Ionicons name={icon} size={is} color={v.text.color as string} />}
          <Text style={[styles.label, { fontSize: fs }, v.text]}>{title}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const VARIANTS: Record<Variant, { container: ViewStyle; text: TextStyle }> = {
  primary: {
    container: { backgroundColor: RD.ink },
    text: { color: '#fff', fontWeight: '600' },
  },
  secondary: {
    container: { backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.borderStrong },
    text: { color: RD.textSec, fontWeight: '600' },
  },
  maroon: {
    container: { backgroundColor: RD.maroon },
    text: { color: '#fff', fontWeight: '600' },
  },
  outline: {
    container: { backgroundColor: 'transparent', borderWidth: 1.5, borderColor: RD.borderStrong },
    text: { color: RD.textSec, fontWeight: '600' },
  },
  danger: {
    container: { backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.danger },
    text: { color: RD.danger, fontWeight: '600' },
  },
  ghost: {
    container: { backgroundColor: 'transparent' },
    text: { color: RD.ink, fontWeight: '600' },
  },
};

const styles = StyleSheet.create({
  base:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 20 },
  label:     { letterSpacing: 0.2, fontFamily: fonts.semiBold },
  fullWidth: { width: '100%' },
  disabled:  { opacity: 0.4 },
});
